import { format } from 'date-fns';
import { VFC, useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import {
  Checkbox,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  makeStyles,
} from '@material-ui/core';
import { MoreVert } from '@material-ui/icons';

import { Application } from '../../../common/content-types';
import { LibraryItem, isAlbumItem } from '../../../components/document/types';
import { useProject } from '../../../contexts/project-context';
import { ItemIcon } from './item-icon';

const useStyles = makeStyles((theme) => ({
  listItem: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  listItemWithBorder: {
    borderTop: '1px solid rgba(0,0,0,0.12)',
  },
  listItemIcon: {
    paddingRight: theme.spacing(1),
  },
  nameWithCount: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

interface DocumentListItemProps {
  item: LibraryItem;
  setSelectedItem: (item: LibraryItem) => void;
  setAnchorEl: (el: HTMLElement | null) => void;
  showSelection: boolean;
  selectedItemPaths?: string[];
  setSelectedItemPaths?: (paths: string[]) => void;
  index: number;
  showActions?: boolean;
  moveCopyView?: boolean;
}

export const DocumentListItem: VFC<DocumentListItemProps> = ({
  item,
  setSelectedItem,
  setAnchorEl,
  showSelection,
  selectedItemPaths,
  setSelectedItemPaths,
  index,
  showActions = true,
  moveCopyView,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const project = useProject();

  const itemUrl = isAlbumItem(item) ? undefined : item.appUrl ?? item.webUrl;

  const clickable =
    isAlbumItem(item) ||
    item.contentType === Application.MetronFolder ||
    itemUrl != null;

  const handleClick = useCallback(() => {
    if (isAlbumItem(item)) {
      history.push(`/project/${project.projectKey}/albums/${item.id}`);
      return;
    }

    if (item.contentType === Application.MetronFolder) {
      if (moveCopyView) {
        setSelectedItem(item);
        return;
      }
      history.push(
        `/project/${project.projectKey}/documents${item.path}/${item.name}`
      );
    } else if (itemUrl != null) {
      const linkElement = document.createElement('a');
      linkElement.href = itemUrl;
      linkElement.target = '_blank';
      linkElement.rel = 'noopener noreferrer';
      linkElement.click();
    }
  }, [
    history,
    item,
    moveCopyView,
    itemUrl,
    project.projectKey,
    setSelectedItem,
  ]);

  const modifiedAt = new Date(item.modifiedAt);

  const content = [
    <ListItemIcon key='icon' className={classes.listItemIcon}>
      <ItemIcon
        contentType={
          isAlbumItem(item) ? Application.MetronAlbum : item.contentType
        }
      />
    </ListItemIcon>,
    <ListItemText
      key='text'
      primary={
        <span
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span className={classes.nameWithCount}>
            {item.name}
            {isAlbumItem(item) && (
              <span style={{ color: '#888', fontSize: '12px' }}>
                {item.activeImageCount} bilder
              </span>
            )}
          </span>
          <span
            style={{
              fontSize: '12px',
              color: '#888',
            }}
          >
            {isAlbumItem(item) ? item.modifiedBy.name : item.modifiedBy}
            <>&nbsp;</>
            {format(modifiedAt, 'yyyy-MM-dd HH:mm')}
          </span>
        </span>
      }
    />,

    showActions && !moveCopyView && (
      <ListItemSecondaryAction key='actions'>
        {showSelection ? (
          <Checkbox
            color='primary'
            onChange={(value) => {
              if (value.target.checked) {
                setSelectedItemPaths?.([
                  ...(selectedItemPaths ?? []),
                  item.path + '/' + item.name,
                ]);
              } else {
                setSelectedItemPaths?.(
                  (selectedItemPaths ?? []).filter(
                    (path) => path !== item.path + '/' + item.name
                  )
                );
              }
            }}
            checked={(selectedItemPaths ?? []).includes(
              item.path + '/' + item.name
            )}
          />
        ) : (
          <IconButton
            onClick={(event) => {
              setSelectedItem(item);
              setAnchorEl(event.currentTarget);
            }}
          >
            <MoreVert />
          </IconButton>
        )}
      </ListItemSecondaryAction>
    ),
  ];

  return clickable || moveCopyView ? (
    <ListItem
      button
      className={`${classes.listItem} ${
        index === 0 ? '' : classes.listItemWithBorder
      }`}
      onClick={handleClick}
    >
      {content}
    </ListItem>
  ) : (
    <ListItem
      className={`${classes.listItem} ${
        index === 0 ? '' : classes.listItemWithBorder
      }`}
    >
      {content}
    </ListItem>
  );
};
